import type { CheckInTemplate, Tag, Track } from '@anytracker/core'

import { Button } from '../../components/Button.js'
import { Sheet, SheetOptionGroup, type SheetOption } from '../../components/Sheet.js'
import styles from './Timeline.module.css'

export interface Filters {
  readonly trackId: string
  readonly tagId: string
  readonly templateId: string
}

/** Which axes are set. The number beside the filter button. */
export function activeFilterCount(filters: Filters): number {
  return [filters.trackId, filters.tagId, filters.templateId].filter((id) => id !== '')
    .length
}

interface Props {
  readonly open: boolean
  readonly filters: Filters
  /** Archived included — see below. */
  readonly tracks: ReadonlyMap<string, Track>
  readonly tags: ReadonlyMap<string, Tag>
  readonly templates: ReadonlyMap<string, CheckInTemplate>
  readonly onChange: (axis: keyof Filters, id: string) => void
  readonly onClearAll: () => void
  /** Whether anything narrows the list, the range included. */
  readonly canClear: boolean
  readonly onClose: () => void
}

/**
 * Narrowing history, on three axes (T-905).
 *
 * Check-in is the new one and the one most likely to be wanted: a template is
 * how people think about what they logged, and "only my evening check-ins" was
 * not previously expressible.
 *
 * **Archived tracks and tags stay in the list.** Archiving removes something
 * from *logging*, not from history — a filter list that omits them makes months
 * of recorded measurements unreachable from the one screen meant to show
 * everything.
 *
 * Nothing here confirms. Choosing an option applies it and the list behind the
 * sheet changes; there is no state to commit, so a "Done" that implied one
 * would be a lie about what the buttons already did.
 */
export function FilterSheet({
  open,
  filters,
  tracks,
  tags,
  templates,
  onChange,
  onClearAll,
  canClear,
  onClose,
}: Props) {
  const group = (
    axis: keyof Filters,
    anyLabel: string,
    items: readonly { id: string; label: string; colour?: string }[],
  ): readonly SheetOption[] => [
    {
      id: '',
      label: anyLabel,
      selected: filters[axis] === '',
      onChoose: () => {
        onChange(axis, '')
      },
    },
    ...items.map((item) => ({
      id: item.id,
      label: item.label,
      ...(item.colour === undefined ? {} : { colour: item.colour }),
      selected: filters[axis] === item.id,
      onChoose: () => {
        onChange(axis, item.id)
      },
    })),
  ]

  return (
    <Sheet open={open} title="Filter" onClose={onClose}>
      <SheetOptionGroup
        label="Track"
        options={group(
          'trackId',
          'Any track',
          [...tracks.values()].map((track) => ({
            id: track.id,
            label: track.archived ? `${track.name} (archived)` : track.name,
          })),
        )}
      />
      <SheetOptionGroup
        label="Tag"
        options={group(
          'tagId',
          'Any tag',
          [...tags.values()].map((tag) => ({
            id: tag.id,
            label: tag.name,
            ...(tag.colour === undefined ? {} : { colour: tag.colour }),
          })),
        )}
      />
      <SheetOptionGroup
        label="Check-in"
        options={group(
          'templateId',
          'Any check-in',
          [...templates.values()].map((template) => ({
            id: template.id,
            label: template.archived ? `${template.name} (archived)` : template.name,
          })),
        )}
      />

      {/* Only when there is something to clear. A permanently-present button
          that usually does nothing teaches people it does nothing. */}
      {canClear && (
        <div className={styles['clearAll']}>
          <Button onClick={onClearAll}>Clear all filters</Button>
        </div>
      )}
    </Sheet>
  )
}
